/* One Bioactive – Bioactive Build Planner
   Species → animal count → enclosure size → layers, products, bundle, guides, care & cleaning. */
(() => {
  'use strict';
  const root = document.querySelector('[data-planner]');
  if (!root || !window.OB_SPECIES) return;

  const BLOG = '/blogs/the-benefits-of-having-a-completely-bioactive-terrarium-for-dart-frogs/';
  const $ = (s, el = root) => el.querySelector(s);
  const $$ = (s, el = root) => Array.from(el.querySelectorAll(s));
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (cents) => '$' + (cents / 100).toFixed(2);
  const r1 = (n) => Math.round(n * 10) / 10;

  /* ---------- Species ---------- */
  const GROUPS = { R: 'Reptile', A: 'Amphibian', S: 'Arachnid', I: 'Insect & invert' };
  const LEVELS = { B: 'Beginner friendly', I: 'Intermediate', A: 'Advanced' };
  const SPECIES = window.OB_SPECIES.map((r) => ({
    slug: r[0], name: r[1], latin: r[2], group: r[3], build: r[4],
    L: r[5], W: r[6], H: r[7], max: r[8], extra: r[9],
    temp: r[10], hum: r[11], soil: r[12], level: r[13], note: r[14]
  })).sort((a, b) => a.name.localeCompare(b.name));
  const bySlug = Object.fromEntries(SPECIES.map((s) => [s.slug, s]));

  /* ---------- Enclosure presets (cm) ---------- */
  const PRESETS = [
    [20, 20, 20], [20, 20, 30], [30, 20, 20], [30, 30, 30], [30, 30, 45], [45, 30, 30], [45, 45, 45], [45, 45, 60],
    [60, 30, 30], [60, 45, 45], [60, 45, 60], [60, 45, 90], [60, 60, 120], [90, 45, 45], [90, 45, 60], [90, 45, 90],
    [90, 60, 120], [120, 60, 60], [120, 60, 90], [120, 60, 120], [150, 60, 60], [180, 60, 60], [180, 90, 90],
    [180, 90, 180], [240, 120, 120]
  ];
  const inch = (cm) => Math.round(cm / 2.54);
  const presetLabel = ([l, w, h]) => `${l} × ${w} × ${h} cm (${inch(l)} × ${inch(w)} × ${inch(h)}″)`;

  /* ---------- Build types ---------- */
  const G = (h, t) => ({ href: BLOG + h, title: t });
  const GUIDES = {
    dart: G('how-to-build-a-dart-frog-vivarium-with-cork-bark', 'How to build a dart frog vivarium with cork bark'),
    crested: G('how-to-build-a-bioactive-crested-gecko-vivarium', 'How to build a bioactive crested gecko vivarium'),
    tropical: G('how-to-build-a-tropical-terrarium-with-cork-bark-and-spiderwood', 'Tropical build with cork bark and spiderwood'),
    background: G('how-to-build-a-cork-bark-background', 'How to build a cork bark background'),
    leo: G('how-to-build-a-bioactive-leopard-gecko-enclosure', 'How to build a bioactive leopard gecko enclosure'),
    bp: G('how-to-build-a-bioactive-ball-python-enclosure', 'How to build a bioactive ball python enclosure'),
    palu: G('how-to-build-a-paludarium-with-driftwood', 'How to build a paludarium with driftwood'),
    crew: G('springtails-and-isopods-clean-up-crew-guide', 'Springtails & isopods: the clean-up crew guide'),
    litter: G('best-leaf-litter-for-isopods-and-bioactive-terrariums-compared', 'Best leaf litter for isopods, compared'),
    mould: G('mould-in-a-bioactive-terrarium-whats-normal-and-how-to-fix-it', 'Mould in a bioactive terrarium: what’s normal'),
    growin: G('how-to-grow-in-a-bioactive-terrarium-before-adding-animals', 'How to grow in a bioactive before adding animals'),
    drainage: G('drainage-layer-guide-clay-balls-vs-false-bottom', 'Drainage layer guide: clay balls vs false bottom'),
    humidity: G('humidity-and-ventilation-in-a-bioactive-terrarium', 'Humidity & ventilation in a bioactive terrarium'),
    winter: G('keeping-a-bioactive-terrarium-healthy-through-a-canadian-winter', 'Keeping a bioactive healthy through a Canadian winter'),
    desertPlants: G('best-plants-for-desert-and-semi-arid-bioactive-enclosures', 'Best plants for desert & semi-arid bioactives'),
    rocks: G('using-rocks-and-stone-in-a-bioactive-terrarium', 'Using rocks and stone safely'),
    safePlants: G('reptile-safe-plants-toxicity-guide', 'Reptile-safe plants: toxicity guide'),
    bromeliads: G('bromeliads-in-vivariums-mounting-and-care', 'Bromeliads in vivariums'),
    mount: G('how-to-mount-plants-on-cork-bark-and-wood', 'How to mount plants on cork bark and wood'),
    pothos: G('best-pothos-and-philodendron-for-terrariums', 'Best pothos & philodendron for terrariums'),
    woodprep: G('how-to-clean-and-prepare-wood-for-a-terrarium', 'How to clean and prepare terrarium wood'),
    abg: G('what-is-abg-mix-the-complete-guide-to-bioactive-soil-in-canada', 'What is ABG mix?'),
    lighting: G('terrarium-lighting-for-plants-led-par-kelvin', 'Terrarium lighting for plants'),
    moss: G('how-to-keep-live-moss-green-in-a-terrarium', 'How to keep live moss green'),
    hardscape: G('terrarium-hardscape-design-composition', 'Terrarium hardscape design'),
    tarantula: { href: '/pages/creating-a-bioactive-haven-for-tarantulas', title: 'Creating a bioactive haven for tarantulas' }
  };

  // Product slots. `alt` = fallback handles if the first is unavailable.
  // qty types: 'litres' (fill), 'fixed', 'area' (per floor area), 'size' (pick variant by enclosure dimension)
  const P = {
    clay: 'clay-bio-balls', abg: 'jurassic-abg-terrarium-mix', arid: 'jurassic-abg-semi-arid-blend', tara: 'jurassic-abg-tarantula-mix',
    canyon: 'canyon-sand', desert: 'desert-sand-orange', cypress: 'cypress-substrate', chips: 'coco-husk-chips-loose', coir: 'coconut-fiber'
  };

  const SHARED_CLEAN = [
    'Spot-clean droppings, shed skin and uneaten food as you see them. Your clean-up crew handles the small stuff, but large waste should be removed.',
    'Wipe the glass with warm water or a reptile-safe cleaner. Never spray household chemicals inside the enclosure.',
    'Leave white fungal fuzz in the first weeks alone. It is part of the break-in and springtails clear it.',
    'A full strip-down is only needed after a disease outbreak, a mite infestation, flooding with sour-smelling soil, or a change of species. Otherwise a healthy bioactive should never need a complete substrate change.'
  ];

  const BUILDS = {
    TF: {
      label: 'Tropical forest floor', habitat: 'tropical',
      drainage: (H) => (H < 40 ? 3 : H < 70 ? 4 : 5), soil: 8, soilProduct: 'abg',
      litter: { handles: ['magnolia-leaves-10-pack', 'almond-leaves'], per: 1600 },
      moss: ['fresh-sheet-moss', 'fresh-frog-moss'],
      hardscape: [
        { slot: 'Cork background', handle: 'cork-bark-backgrounds', type: 'background', minH: 40, why: 'Climbing surface and planting wall' },
        { slot: 'Ground hide', handle: 'cork-bark-partial-rounds', type: 'size', dim: 'L', frac: 0.3, qty: 1, why: 'Shaded, humid retreat at floor level' },
        { slot: 'Low branch', handle: 'red-mekong-spiderwood', alt: ['amur-spiderwood'], type: 'size', dim: 'L', frac: 0.6, qty: 1, optional: true, why: 'Perches and depth' }
      ],
      layersNote: 'Deep leaf litter (3–5 cm) matters more here than anything else: it’s where microfauna breed and frogs forage.',
      guides: ['dart', 'bromeliads', 'crew', 'growin', 'humidity', 'moss'],
      care: {
        daily: ['Mist 1–3 times to keep humidity in range; glass should clear partly between mistings.', 'Check temperature – most tropical species are stressed above 27–28°C.'],
        weekly: ['Feed and remove uneaten food.', 'Check the drainage layer through the glass and remove water before it reaches the soil.', 'Prune plants that block the front glass.'],
        monthly: ['Top up leaf litter as it breaks down.', 'Check springtail and isopod numbers; reseed from a culture if they look sparse.', 'Clean misting nozzles and wipe light covers.'],
        yearly: ['Refresh the top few centimetres of soil in open areas.', 'Replace UVB bulbs (if used) on the manufacturer’s schedule.']
      }
    },
    TA: {
      label: 'Tropical arboreal', habitat: 'tropical',
      drainage: (H) => (H < 40 ? 3 : H < 70 ? 4 : 5), soil: 8, soilProduct: 'abg',
      litter: { handles: ['magnolia-leaves-10-pack', 'red-oak-leaves'], per: 2000 },
      moss: ['fresh-sphagnum-moss-container', 'fresh-sheet-moss'],
      hardscape: [
        { slot: 'Cork background', handle: 'cork-bark-backgrounds', type: 'background', minH: 40, why: 'Turns the back wall into climbing space' },
        { slot: 'Vertical cork tube', handle: 'cork-bark-tubes', type: 'size', dim: 'H', frac: 0.7, qty: 1, why: 'A “tree trunk” to climb and hide in' },
        { slot: 'Diagonal branches', handle: 'yamuna-spiderwood', alt: ['red-mekong-spiderwood', 'cork-branches'], type: 'size', dim: 'D', frac: 0.85, qty: (d) => (d.H >= 90 ? 2 : 1), why: 'Arboreal species prefer diagonal and horizontal perches' },
        { slot: 'Extra perch', handle: 'cork-branches', type: 'size', dim: 'L', frac: 0.8, qty: 1, optional: true, why: 'More climbing routes' }
      ],
      layersNote: 'Most of the action is vertical – keep the soil moist but not wet, and focus on perches and dense planting.',
      guides: ['crested', 'tropical', 'background', 'pothos', 'mount', 'humidity'],
      care: {
        daily: ['Mist to raise humidity – usually a heavy evening misting with a dry-down by day.', 'Check temperatures at perch height.'],
        weekly: ['Feed; remove uneaten food (and diet dishes after 24–48 h).', 'Check the drainage layer and prune vines.', 'Look for stuck shed on toes and tails.'],
        monthly: ['Top up leaf litter.', 'Check branches and mounts are still secure.', 'Clean glass, vents and misting nozzles.'],
        yearly: ['Refresh the top of the soil in open areas.', 'Replace UVB bulbs (if used) on schedule.']
      }
    },
    TH: {
      label: 'Humid terrestrial', habitat: 'tropical',
      drainage: (H) => (H >= 45 ? 3 : 0), soil: 12, soilProduct: 'abg', topper: { key: 'cypress', depth: 3, optional: true, label: 'Optional top layer (cypress)' },
      litter: { handles: ['red-oak-leaves', 'magnolia-leaves-10-pack'], per: 2200 },
      moss: ['fresh-sphagnum-moss-container'],
      hardscape: [
        { slot: 'Hides (warm and cool end)', handle: 'cork-bark-tubes', alt: ['cork-bark-partial-rounds'], type: 'size', dim: 'L', frac: 0.3, qty: 2, why: 'Secure hides at both ends of the gradient' },
        { slot: 'Climbing branch', handle: 'malaysian-driftwood', alt: ['red-mekong-spiderwood'], type: 'size', dim: 'L', frac: 0.5, qty: 1, why: 'Sturdy enrichment for heavy-bodied animals' }
      ],
      layersNote: 'Deep, moist soil is what holds humidity in this build – much better than thin bedding, especially through Canadian winters.',
      guides: ['bp', 'drainage', 'humidity', 'crew', 'winter', 'hardscape'],
      care: {
        daily: ['Check warm and cool side temperatures and humidity.', 'Refresh the water bowl.'],
        weekly: ['Mist or pour water into the substrate to keep the lower layers moist.', 'Spot-clean large waste promptly.'],
        monthly: ['Top up leaf litter.', 'Stir and fluff compacted topsoil around hides.', 'Check heat sources and thermostat calibration.'],
        yearly: ['Partially refresh soil in high-traffic areas.', 'Replace UVB bulbs (if used).']
      }
    },
    TM: {
      label: 'Temperate forest', habitat: 'temperate',
      drainage: () => 0, soil: 10, soilProduct: 'abg', topper: { key: 'chips', depth: 3, optional: true, label: 'Optional top layer (coco husk chips)' },
      litter: { handles: ['red-oak-leaves', 'eastern-oak-leaves-10-pack'], per: 2400 },
      moss: [],
      hardscape: [
        { slot: 'Hides (warm and cool end)', handle: 'cork-bark-flats', alt: ['cork-bark-partial-rounds'], type: 'size', dim: 'L', frac: 0.3, qty: 2, why: 'A hide in each temperature zone' },
        { slot: 'Climbing branch', handle: 'cork-branches', alt: ['red-mekong-spiderwood'], type: 'size', dim: 'L', frac: 0.8, qty: 1, why: 'Many colubrids climb more than people expect' }
      ],
      layersNote: 'Keep a moisture gradient: drier on top and at the warm end, slightly moist underneath and at the cool end.',
      guides: ['hardscape', 'crew', 'growin', 'safePlants', 'abg', 'mould'],
      care: {
        daily: ['Check temperatures and refresh water.'],
        weekly: ['Lightly moisten the cool end and lower soil.', 'Spot-clean waste.'],
        monthly: ['Top up leaf litter; check hides and branches are stable.'],
        yearly: ['Partially refresh soil in high-traffic areas.', 'Replace UVB bulbs (if used).']
      }
    },
    SA: {
      label: 'Semi-arid', habitat: 'arid',
      drainage: () => 0, soil: 12, soilProduct: 'arid', sandMix: { key: 'canyon', frac: 0.2, optional: true },
      litter: { handles: ['eastern-oak-leaves-10-pack'], per: 6000 },
      moss: ['terrarium-moss-7l-bag'], mossLabel: 'Humid hide moss',
      hardscape: [
        { slot: 'Hides (warm and cool end)', handle: 'cork-bark-partial-rounds', type: 'size', dim: 'L', frac: 0.25, qty: 2, why: 'A hide at each end – plus one humid hide filled with damp moss' },
        { slot: 'Basking / climbing wood', handle: 'mopani-wood', alt: ['sandblasted-grapewood'], type: 'size', dim: 'L', frac: 0.4, qty: 1, why: 'Climbing and a raised basking spot' },
        { slot: 'Flat basking stone', handle: 'carnelian-stone', alt: ['red-zebra-stone'], type: 'fixed', qty: 3, optional: true, why: 'Holds heat under the lamp – place directly on the enclosure floor' }
      ],
      layersNote: 'No drainage layer – water deeply but rarely so the bottom stays slightly moist while the surface stays dry.',
      guides: ['leo', 'desertPlants', 'rocks', 'safePlants', 'abg', 'crew'],
      care: {
        daily: ['Check basking surface temperature with an infrared thermometer.', 'Keep the humid hide damp; refresh water.'],
        weekly: ['Spot-clean waste.', 'Every 1–2 weeks, pour water down into the soil around plants and in the cool corner.'],
        monthly: ['Check stones and wood are stable and can’t be tunnelled under.', 'Add a few leaves to the cool corner for isopods.'],
        yearly: ['Refresh the top layer in the basking area.', 'Replace UVB bulbs on schedule.']
      }
    },
    AR: {
      label: 'Arid desert', habitat: 'arid',
      drainage: () => 0, soil: 15, soilProduct: 'arid', sandMix: { key: 'canyon', alt: 'desert', frac: 0.4, optional: false },
      litter: null, moss: [],
      hardscape: [
        { slot: 'Hide', handle: 'cork-bark-partial-rounds', type: 'size', dim: 'L', frac: 0.25, qty: 2, why: 'Warm and cool retreats' },
        { slot: 'Wood', handle: 'sandblasted-grapewood', alt: ['mopani-wood'], type: 'size', dim: 'L', frac: 0.4, qty: 1, optional: true, why: 'Climbing and visual structure' },
        { slot: 'Stones', handle: 'red-zebra-stone', alt: ['carnelian-stone'], type: 'fixed', qty: 3, optional: true, why: 'Basking platform and rock crevices' }
      ],
      layersNote: 'A deep, sandy mix packed firmly lets burrows hold their shape; keep only the deepest layer faintly moist.',
      guides: ['desertPlants', 'rocks', 'leo', 'abg'],
      care: {
        daily: ['Check basking temperatures; refresh water (or mist lightly for species that drink droplets).'],
        weekly: ['Spot-clean waste.'],
        monthly: ['Lightly moisten one deep corner so burrows hold and isopods survive.', 'Check rock stacks are stable.'],
        yearly: ['Refresh the top layer; replace UVB bulbs on schedule.']
      }
    },
    SAQ: {
      label: 'Semi-aquatic (paludarium)', habitat: 'tropical', landFrac: 0.5,
      drainage: () => 4, soil: 8, soilProduct: 'abg',
      litter: { handles: ['almond-leaves'], per: 2500 },
      moss: ['fresh-sphagnum-moss-container'],
      hardscape: [
        { slot: 'Driftwood (water to land)', handle: 'malaysian-driftwood', alt: ['red-mekong-spiderwood'], type: 'size', dim: 'L', frac: 0.5, qty: 1, why: 'Bridges water and land; easy exit from the water' },
        { slot: 'Land hide', handle: 'cork-bark-flats', type: 'size', dim: 'L', frac: 0.3, qty: 1, why: 'Shelter on the land section' }
      ],
      layersNote: 'Quantities assume about half the floor is land. The water half needs a filter and regular partial water changes.',
      guides: ['palu', 'woodprep', 'mount', 'humidity'],
      care: {
        daily: ['Check water and air temperature; feed according to species.'],
        weekly: ['Change 20–30% of the water with dechlorinated water.', 'Rinse filter sponge in old tank water (never tap water) as needed.'],
        monthly: ['Top up leaf litter on land; prune plants at the waterline.'],
        yearly: ['Service the pump/filter fully.']
      }
    },
    AMB: {
      label: 'Terrestrial amphibian', habitat: 'tropical',
      drainage: (H) => (H < 40 ? 3 : 4), soil: 10, soilProduct: 'abg',
      litter: { handles: ['magnolia-leaves-10-pack', 'red-oak-leaves'], per: 1800 },
      moss: ['fresh-sheet-moss'],
      hardscape: [
        { slot: 'Ground hide', handle: 'cork-bark-partial-rounds', type: 'size', dim: 'L', frac: 0.35, qty: 1, why: 'A dark, damp retreat' }
      ],
      layersNote: 'Burrowing amphibians need soft, moist soil they can dig into – keep it damp, never waterlogged, and provide a shallow water dish.',
      guides: ['growin', 'crew', 'mould', 'drainage', 'litter'],
      care: {
        daily: ['Check temperature (many salamanders need it cool) and keep the water dish clean.'],
        weekly: ['Feed; remove uneaten food.', 'Moisten soil if the surface dries; check the drainage layer.'],
        monthly: ['Top up leaf litter; check crew numbers.'],
        yearly: ['Refresh the top of the soil.']
      }
    },
    TT: {
      label: 'Terrestrial tarantula', habitat: 'invert',
      drainage: () => 0, soil: 10, soilProduct: 'tara',
      litter: { handles: ['mini-oak-leaves-20-pack'], per: 99999 },
      moss: [],
      hardscape: [
        { slot: 'Hide / starter burrow', handle: 'cork-bark-partial-rounds', type: 'size', dim: 'L', frac: 0.4, qty: 1, why: 'Half-buried, it becomes the start of a burrow' }
      ],
      layersNote: 'Keep the gap between the soil surface and the lid no more than about twice the spider’s leg span, to prevent falls.',
      guides: ['tarantula', 'crew', 'mould', 'litter'],
      care: {
        daily: ['Quick visual check – no need to disturb.'],
        weekly: ['Offer food; remove leftovers (boluses) within 24 h.', 'Refill the water dish; overflow it slightly now and then to dampen one corner.'],
        monthly: ['Check crew and moisture; a dry top with a slightly moist lower layer is ideal.'],
        yearly: ['Rehouse into a larger enclosure as the spider grows.']
      },
      extraClean: ['Never disturb a tarantula in pre-moult or moult; leave the moult in place until the spider has hardened (about a week).']
    },
    TB: {
      label: 'Burrowing tarantula', habitat: 'invert',
      drainage: () => 0, soil: 15, soilProduct: 'tara',
      litter: { handles: ['mini-oak-leaves-20-pack'], per: 99999 }, moss: [],
      hardscape: [
        { slot: 'Starter burrow', handle: 'cork-bark-partial-rounds', type: 'size', dim: 'L', frac: 0.4, qty: 1, why: 'Bury half of it in the soil to start a burrow' }
      ],
      layersNote: 'Deep, slightly packed soil lets burrows hold their shape. Moisten the lower layers; the top can be drier.',
      guides: ['tarantula', 'crew', 'mould'],
      care: {
        daily: ['Visual check only.'],
        weekly: ['Offer food at the burrow entrance; remove leftovers.', 'Refill water; moisten one corner so the deep soil stays damp.'],
        monthly: ['Check moisture deep down by pouring a little water at the edge.'],
        yearly: ['Rehouse as the spider grows.']
      },
      extraClean: ['Burrowers may stay hidden for months – that is normal. Avoid digging them out.']
    },
    TAR: {
      label: 'Arboreal tarantula', habitat: 'invert',
      drainage: () => 0, soil: 6, soilProduct: 'tara',
      litter: { handles: ['mini-oak-leaves-20-pack'], per: 99999 },
      moss: ['dry-sphagnum-moss'], mossLabel: 'Sphagnum (humid corner)',
      hardscape: [
        { slot: 'Vertical cork tube', handle: 'cork-bark-tubes', alt: ['cork-bark-flats'], type: 'size', dim: 'H', frac: 0.7, qty: 1, why: 'Arboreal tarantulas web up inside a vertical tube' }
      ],
      layersNote: 'Cross-ventilation is essential – stuffy, stagnant air is the main killer of arboreal tarantulas.',
      guides: ['tarantula', 'humidity', 'mount', 'crew'],
      care: {
        daily: ['Visual check only.'],
        weekly: ['Offer food; remove leftovers.', 'Lightly mist one side of the enclosure or the web, and keep the water dish full.'],
        monthly: ['Check ventilation holes are clear.'],
        yearly: ['Rehouse as the spider grows.']
      },
      extraClean: ['Use long tongs for maintenance and plan escape routes before opening – arboreals are fast.']
    },
    SCT: {
      label: 'Tropical invertebrate', habitat: 'invert',
      drainage: () => 0, soil: 12, soilProduct: 'tara',
      litter: { handles: ['magnolia-leaves-10-pack'], per: 2500 },
      moss: ['dry-sphagnum-moss'], mossLabel: 'Sphagnum (humid corner)',
      hardscape: [
        { slot: 'Hides', handle: 'cork-bark-flats', type: 'size', dim: 'L', frac: 0.35, qty: 2, why: 'Flat bark to shelter and burrow under' }
      ],
      layersNote: 'Keep the soil evenly moist and dig-friendly; a cork slab pressed onto the soil gives a ready-made scrape.',
      guides: ['crew', 'mould', 'litter', 'humidity'],
      care: {
        daily: ['Visual check.'],
        weekly: ['Feed; remove leftovers.', 'Mist one side and keep the water dish full.'],
        monthly: ['Top up leaf litter.'],
        yearly: ['Refresh the top soil if it compacts.']
      }
    },
    SC: {
      label: 'Desert invertebrate', habitat: 'arid',
      drainage: () => 0, soil: 12, soilProduct: 'arid', sandMix: { key: 'canyon', frac: 0.3, optional: false },
      litter: null, moss: [],
      hardscape: [
        { slot: 'Hide', handle: 'cork-bark-partial-rounds', type: 'size', dim: 'L', frac: 0.35, qty: 1, why: 'Shelter and a burrow starting point' }
      ],
      layersNote: 'Keep it dry with one slightly moistened corner. Desert species are sensitive to stuffy, wet conditions.',
      guides: ['desertPlants', 'rocks', 'crew'],
      care: {
        daily: ['Visual check.'],
        weekly: ['Feed; remove leftovers.', 'Moisten one corner lightly.'],
        monthly: ['Check the soil hasn’t become damp throughout.'],
        yearly: ['Refresh substrate if it smells or compacts.']
      }
    },
    IV: {
      label: 'Invertebrate colony', habitat: 'invert',
      drainage: () => 0, soil: 8, soilProduct: 'abg',
      litter: { handles: ['red-oak-leaves', 'magnolia-leaves-10-pack', 'kumbuk-leaves'], per: 900 },
      moss: ['dry-sphagnum-moss'], mossLabel: 'Sphagnum (humid corner)',
      hardscape: [
        { slot: 'Bark slabs', handle: 'cork-bark-flats', type: 'size', dim: 'L', frac: 0.4, qty: 2, why: 'Hides, breeding sites and a food source' }
      ],
      layersNote: 'Leaf litter is food here, not decoration – keep a thick layer topped up. Keep one side damper than the other.',
      guides: ['crew', 'litter', 'mould'],
      care: {
        daily: ['Nothing needed most days.'],
        weekly: ['Mist the damp side; add vegetables or protein food in small amounts and remove leftovers after 24–48 h.'],
        monthly: ['Top up leaf litter and add a calcium source (cuttlebone).', 'Thin out the colony or split it if it gets crowded.'],
        yearly: ['Replace about a third of the soil, which turns into frass over time.']
      }
    },
    IAR: {
      label: 'Arboreal insect', habitat: 'invert',
      drainage: () => 0, soil: 4, soilProduct: 'coir',
      litter: { handles: ['mini-oak-leaves-20-pack'], per: 99999 }, moss: [],
      hardscape: [
        { slot: 'Climbing branches', handle: 'cork-branches', alt: ['amur-spiderwood'], type: 'size', dim: 'H', frac: 0.8, qty: (d) => (d.H >= 45 ? 2 : 1), why: 'Hanging and moulting space – moults need room below the insect' }
      ],
      layersNote: 'A thin, moisture-holding floor layer keeps humidity up. Leave at least twice the insect’s length of clear space below perches for moulting.',
      guides: ['humidity', 'safePlants', 'lighting'],
      care: {
        daily: ['Mist lightly (species dependent) and check food plants are fresh.'],
        weekly: ['Replace food plants; remove frass and old leaves.'],
        monthly: ['Wipe mesh and check for mites.'],
        yearly: ['Replace the floor layer between generations.']
      }
    }
  };

  /* ---------- Product loading ---------- */
  const cache = {};
  const loadProduct = (handle) => (cache[handle] ||= fetch(`/products/${handle}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null));
  const litresOf = (title) => {
    const m = String(title).match(/(\d+(?:\.\d+)?)\s*(l\b|litre|liter)/i);
    if (m) return parseFloat(m[1]);
    const g = String(title).match(/(\d+(?:\.\d+)?)\s*(kg|g)\b/i); // coconut fibre bricks
    if (g) return g[2].toLowerCase() === 'kg' ? parseFloat(g[1]) * 13 : parseFloat(g[1]) * 0.013;
    return null;
  };
  const inchesOf = (title) => {
    const nums = (String(title).match(/\d+(?:\.\d+)?/g) || []).map(Number);
    return nums.length ? Math.max(...nums) : null;
  };
  // cheapest combination of sizes that covers `need` litres
  const fillCombo = (product, need) => {
    const vs = product.variants.filter((v) => v.available && litresOf(v.title));
    if (!vs.length) return null;
    let best = null;
    const rec = (i, left, cost, picks) => {
      if (left <= 0) { if (!best || cost < best.cost) best = { cost, picks: picks.slice() }; return; }
      if (i >= vs.length || (best && cost >= best.cost)) return;
      const L = litresOf(vs[i].title);
      const maxN = Math.min(12, Math.ceil(left / L));
      for (let n = maxN; n >= 0; n--) {
        if (n) picks.push({ variant: vs[i], qty: n });
        rec(i + 1, left - n * L, cost + n * vs[i].price, picks);
        if (n) picks.pop();
      }
    };
    rec(0, need, 0, []);
    return best ? best.picks : null;
  };
  // variant whose size best fits a target length (inches)
  const sizeVariant = (product, targetIn) => {
    const vs = product.variants.filter((v) => v.available);
    if (!vs.length) return null;
    const sized = vs.map((v) => ({ v, n: inchesOf(v.title) })).filter((x) => x.n);
    if (!sized.length) return vs[0];
    sized.sort((a, b) => a.n - b.n);
    const fit = sized.filter((x) => x.n <= targetIn);
    return (fit.length ? fit[fit.length - 1] : sized[0]).v;
  };
  const backgroundVariant = (product, Lcm, Hcm) => {
    const Lin = Lcm / 2.54, Hin = (Hcm - 10) / 2.54;
    const vs = product.variants.filter((v) => v.available).map((v) => {
      const n = (v.title.match(/\d+/g) || []).map(Number);
      return { v, a: Math.min(n[0], n[1] || n[0]), b: Math.max(n[0], n[1] || n[0]) };
    }).filter((x) => x.a);
    if (!vs.length) return null;
    const fits = vs.filter((x) => (x.a <= Lin && x.b <= Hin) || (x.b <= Lin && x.a <= Hin));
    const pickFrom = fits.length ? fits : vs;
    pickFrom.sort((p, q) => p.a * p.b - q.a * q.b);
    const best = pickFrom[pickFrom.length - (fits.length ? 1 : pickFrom.length)];
    const wall = Lin * Hin;
    const qty = Math.max(1, Math.min(4, Math.round(wall / (best.a * best.b))));
    return { variant: best.v, qty };
  };

  /* ---------- State ---------- */
  const state = { species: null, count: 1, dims: null, unit: 'cm' };

  /* ---------- Species picker ---------- */
  const search = $('[data-pl-search]');
  const list = $('[data-pl-list]');
  const select = $('[data-pl-select]');
  const chips = $$('[data-pl-group]');
  let groupFilter = '';
  select.innerHTML = '<option value="">Choose from the list…</option>' + Object.entries(GROUPS).map(([g, label]) =>
    `<optgroup label="${label}s">${SPECIES.filter((s) => s.group === g).map((s) => `<option value="${s.slug}">${esc(s.name)}</option>`).join('')}</optgroup>`).join('');
  $('[data-pl-count-species]').textContent = SPECIES.length;

  const norm = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  let active = -1;
  const renderList = () => {
    const q = norm(search.value.trim());
    let res = SPECIES.filter((s) => (!groupFilter || s.group === groupFilter) && (!q || norm(s.name + ' ' + s.latin).includes(q)));
    if (q) res.sort((a, b) => (norm(a.name).startsWith(q) ? 0 : 1) - (norm(b.name).startsWith(q) ? 0 : 1));
    res = res.slice(0, 40);
    active = -1;
    list.innerHTML = res.length ? res.map((s, i) => `<li role="option" id="pl-opt-${i}" data-slug="${s.slug}"><span class="pl-opt__name">${esc(s.name)}</span><span class="pl-opt__meta"><em>${esc(s.latin)}</em> · ${GROUPS[s.group]}</span></li>`).join('')
      : '<li class="pl-empty">No match – try the common or Latin name, or browse the list.</li>';
    list.hidden = false;
    search.setAttribute('aria-expanded', 'true');
  };
  const closeList = () => { list.hidden = true; search.setAttribute('aria-expanded', 'false'); };
  search.addEventListener('input', renderList);
  search.addEventListener('focus', renderList);
  search.addEventListener('keydown', (e) => {
    const items = $$('li[data-slug]', list);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (list.hidden) renderList();
      active = Math.max(0, Math.min(items.length - 1, active + (e.key === 'ArrowDown' ? 1 : -1)));
      items.forEach((li, i) => li.classList.toggle('is-active', i === active));
      items[active] && (items[active].scrollIntoView({ block: 'nearest' }), search.setAttribute('aria-activedescendant', items[active].id));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const li = items[active] || items[0];
      li && chooseSpecies(li.dataset.slug);
    } else if (e.key === 'Escape') closeList();
  });
  list.addEventListener('mousedown', (e) => { const li = e.target.closest('li[data-slug]'); if (li) { e.preventDefault(); chooseSpecies(li.dataset.slug); } });
  document.addEventListener('click', (e) => { if (!e.target.closest('.pl-search')) closeList(); });
  select.addEventListener('change', () => select.value && chooseSpecies(select.value));
  chips.forEach((c) => c.addEventListener('click', () => {
    groupFilter = c.dataset.plGroup === groupFilter ? '' : c.dataset.plGroup;
    chips.forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.plGroup === groupFilter)));
    renderList(); search.focus();
  }));

  /* ---------- Steps ---------- */
  const countIn = $('[data-pl-count]');
  const countAdvice = $('[data-pl-count-advice]');
  const presetSel = $('[data-pl-preset]');
  const dimIns = { L: $('[data-pl-l]'), W: $('[data-pl-w]'), H: $('[data-pl-h]') };
  const sizeAdvice = $('[data-pl-size-advice]');
  presetSel.innerHTML = PRESETS.map((p) => `<option value="${p.join('x')}">${presetLabel(p)}</option>`).join('') + '<option value="custom">Custom size…</option>';

  const effectiveCount = () => {
    const s = state.species; if (!s) return 1;
    if (s.max === 99) return 1; // colonies scale by enclosure, not head count
    return Math.max(1, Math.min(state.count, s.max));
  };
  const needed = () => {
    const s = state.species, n = effectiveCount();
    const base = s.max > 1 ? Math.min(2, s.max) : 1; // minimum sizes already suit a pair for social species
    const area = s.L * s.W * (1 + s.extra * Math.max(0, n - base));
    const scale = Math.sqrt(area / (s.L * s.W));
    return { L: s.L * scale, W: s.W * scale, H: s.H, area };
  };
  const suggest = () => {
    const nd = needed();
    const sp = state.species; const ok = PRESETS.filter(([l, w, h]) => l >= sp.L && w >= sp.W && h >= sp.H && l * w >= nd.area * 0.98);
    ok.sort((a, b) => a[0] * a[1] * a[2] - b[0] * b[1] * b[2]);
    return ok[0] || null;
  };

  const chooseSpecies = (slug) => {
    const s = bySlug[slug]; if (!s) return;
    state.species = s;
    search.value = s.name; select.value = slug; closeList();
    $('[data-pl-chosen]').innerHTML = `<strong>${esc(s.name)}</strong> <em>${esc(s.latin)}</em> <span class="pl-tag">${GROUPS[s.group]}</span><span class="pl-tag pl-tag--${s.level}">${LEVELS[s.level]}</span>`;
    root.classList.add('has-species');
    const colony = s.max === 99;
    $('[data-pl-count-label]').textContent = colony ? 'How big is your starter colony?' : `How many ${s.name.toLowerCase()}s will share this enclosure?`;
    countIn.value = colony ? 20 : 1; state.count = Number(countIn.value);
    updateCount();
    const sug = suggest();
    if (sug) { presetSel.value = sug.join('x'); setDims(sug); } else { presetSel.value = 'custom'; setDims([Math.ceil(needed().L / 10) * 10, Math.ceil(needed().W / 10) * 10, s.H]); }
    $('[data-pl-step="2"]').hidden = false; $('[data-pl-step="3"]').hidden = false;
    updateURL(); render();
    if (window.innerWidth < 990) $('[data-pl-step="2"]').scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const updateCount = () => {
    const s = state.species; if (!s) return;
    const n = Math.max(1, Math.round(Number(countIn.value) || 1)); state.count = n;
    let html = '', cls = 'ok';
    if (s.max === 99) { html = `A <strong>colony species</strong> – they live happily in groups. Start with 10–30 and they’ll grow to fit the space.`; }
    else if (s.max === 1 && n > 1) { cls = 'warn'; html = `<strong>Keep ${esc(s.name)}s one per enclosure.</strong> ${esc(s.note)} Everything below is planned for <strong>one animal</strong> – you’ll need ${n} separate enclosures (multiply the shopping list by ${n}).`; }
    else if (n > s.max) { cls = 'warn'; html = `<strong>We recommend no more than ${s.max} per enclosure.</strong> ${esc(s.note)} The plan below is for ${s.max}.`; }
    else if (s.max === 1) { html = `<strong>Solitary species – one per enclosure is right.</strong> ${esc(s.note)}`; }
    else { html = `<strong>Yes, ${n} can share</strong> (up to ${s.max} recommended). ${esc(s.note)}`; }
    countAdvice.className = 'pl-advice pl-advice--' + cls; countAdvice.innerHTML = html;
  };
  countIn.addEventListener('input', () => {
    updateCount();
    const sug = suggest();
    if (sug && presetSel.value !== 'custom') { presetSel.value = sug.join('x'); setDims(sug); }
    updateURL(); render();
  });
  $$('[data-pl-step-count]').forEach((b) => b.addEventListener('click', () => { countIn.value = Math.max(1, (Number(countIn.value) || 1) + Number(b.dataset.plStepCount)); countIn.dispatchEvent(new Event('input')); }));

  const toUnit = (cm) => (state.unit === 'in' ? r1(cm / 2.54) : Math.round(cm));
  const setDims = ([l, w, h]) => { state.dims = { L: l, W: w, H: h }; dimIns.L.value = toUnit(l); dimIns.W.value = toUnit(w); dimIns.H.value = toUnit(h); };
  presetSel.addEventListener('change', () => { if (presetSel.value !== 'custom') { setDims(presetSel.value.split('x').map(Number)); updateURL(); render(); } else dimIns.L.focus(); });
  Object.values(dimIns).forEach((inp) => inp.addEventListener('input', () => {
    const k = state.unit === 'in' ? 2.54 : 1;
    state.dims = { L: (Number(dimIns.L.value) || 0) * k, W: (Number(dimIns.W.value) || 0) * k, H: (Number(dimIns.H.value) || 0) * k };
    presetSel.value = 'custom'; updateURL(); render();
  }));
  $$('input[name="pl-unit"]').forEach((r) => r.addEventListener('change', () => { state.unit = r.value; state.dims && setDims([state.dims.L, state.dims.W, state.dims.H]); $$('[data-pl-unit-label]').forEach((el) => (el.textContent = state.unit)); }));
  $('[data-pl-use-suggested]').addEventListener('click', () => { const sug = suggest(); if (sug) { presetSel.value = sug.join('x'); setDims(sug); updateURL(); render(); } });

  /* ---------- URL state (shareable plans) ---------- */
  const updateURL = () => {
    if (!state.species || !state.dims) return;
    const p = new URLSearchParams({ species: state.species.slug, n: state.count, size: [state.dims.L, state.dims.W, state.dims.H].map(Math.round).join('x') });
    history.replaceState(null, '', `${location.pathname}?${p}`);
  };

  /* ---------- Render plan ---------- */
  const out = $('[data-pl-result]');
  let renderToken = 0;
  let bundle = [];

  const render = async () => {
    const s = state.species, d = state.dims;
    if (!s || !d || !d.L || !d.W || !d.H) return;
    const token = ++renderToken;
    const b = BUILDS[s.build];
    const n = effectiveCount();
    const nd = needed();
    const sug = suggest();

    // Size verdict
    const small = d.L < s.L * 0.95 || d.W < s.W * 0.95 || d.H < s.H * 0.95 || d.L * d.W < nd.area * 0.95;
    sizeAdvice.className = 'pl-advice pl-advice--' + (small ? 'warn' : 'ok');
    const who = n > 1 ? n + ' ' + esc(s.name) + 's' : 'an adult ' + esc(s.name);
    const minTxt = `${s.L} × ${s.W} × ${s.H} cm` + (n > 1 ? `, with about ${(nd.area / 10000).toFixed(2)} m² of floor for ${n}` : '');
    sizeAdvice.innerHTML = (small
      ? `<strong>Too small for ${who}.</strong> Minimum we recommend: ${minTxt}.`
      : `<strong>Good size</strong> for ${who} (minimum ${minTxt}). Bigger is always better.`)
      + (sug ? ` <span class="muted">Suggested: ${presetLabel(sug)}.</span>` : ' <span class="muted">This species needs a custom-built enclosure.</span>');

    // Layers
    const landFrac = b.landFrac || 1;
    const area = d.L * d.W * landFrac; // cm²
    const drainCm = b.drainage(d.H);
    let soilCm = s.soil || b.soil;
    if (s.build === 'TT' || s.build === 'TB') soilCm = Math.min(soilCm, Math.max(5, d.H - 15)); // keep a safe climb gap
    const layers = [];
    if (b.litter) layers.push({ key: 'litter', name: 'Leaf litter', cm: s.build === 'IV' || s.build === 'TF' ? 4 : 2, color: '#8A6A3A' });
    if (b.moss && b.moss.length && b.habitat === 'tropical') layers.push({ key: 'moss', name: 'Live moss patches', cm: 1, color: '#6E8F4E', thin: true });
    if (b.topper) layers.push({ key: 'topper', name: b.topper.label, cm: b.topper.depth, color: '#B07A47', optional: true });
    layers.push({ key: 'soil', name: soilName(b), cm: soilCm, color: b.habitat === 'arid' ? '#B98552' : '#4A3727' });
    if (drainCm) { layers.push({ key: 'mesh', name: 'Mesh barrier', cm: 0.5, color: '#9AA39A', thin: true }); layers.push({ key: 'drain', name: 'Clay Bio Balls (drainage)', cm: drainCm, color: '#C98258' }); }

    const soilL = (area * soilCm) / 1000 * 1.1;
    const drainL = (area * drainCm) / 1000;

    // Product lines
    const lines = [];
    const add = async (role, handle, opts) => {
      const handles = [handle].concat(opts.alt || []);
      for (const h of handles) {
        const p = await loadProduct(h);
        if (!p) continue;
        let picks = null;
        if (opts.litres != null) picks = fillCombo(p, opts.litres);
        else if (opts.size != null) { const v = sizeVariant(p, opts.size); picks = v ? [{ variant: v, qty: opts.qty || 1 }] : null; }
        else if (opts.background) { const r = backgroundVariant(p, d.L, d.H); picks = r ? [r] : null; }
        else { const v = p.variants.find((x) => x.available); picks = v ? [{ variant: v, qty: opts.qty || 1 }] : null; }
        if (picks && picks.length) { lines.push({ role, product: p, handle: h, picks, optional: !!opts.optional, why: opts.why || '', amount: opts.amount || '' }); return; }
      }
      if (!opts.optional) lines.push({ role, missing: true, handle, why: 'Currently out of stock – check back soon.' });
    };

    const jobs = [];
    if (drainCm) jobs.push(add('Drainage layer', P.clay, { litres: drainL, amount: `${r1(drainL)} L for ${drainCm} cm`, why: 'Keeps excess water away from roots and soil' }));
    let soilMain = soilL, sandL = 0;
    if (b.sandMix) { sandL = soilL * b.sandMix.frac; if (!b.sandMix.optional) soilMain = soilL - sandL; }
    const soilProduct = P[b.soilProduct];
    jobs.push(add('Bioactive soil', soilProduct, { litres: soilMain, amount: `${r1(soilMain)} L for ${soilCm} cm (incl. 10% for settling)`, why: soilWhy(b) }));
    if (b.sandMix) jobs.push(add(b.sandMix.optional ? 'Sand (optional, firmer surface)' : 'Sand for the mix', P[b.sandMix.key], { alt: b.sandMix.alt ? [P[b.sandMix.alt]] : [], litres: sandL, optional: b.sandMix.optional, amount: `${r1(sandL)} L`, why: b.sandMix.optional ? 'Mix about 1 part sand to 4 parts soil for a firmer surface' : `Mix about ${Math.round(b.sandMix.frac * 10)} parts sand to ${10 - Math.round(b.sandMix.frac * 10)} parts soil` }));
    if (b.topper) { const tL = (area * b.topper.depth) / 1000; jobs.push(add('Top layer (optional)', P[b.topper.key], { litres: tL, optional: true, amount: `${r1(tL)} L for ${b.topper.depth} cm`, why: 'Holds surface humidity around hides' })); }
    if (b.litter) {
      const packs = Math.max(1, Math.ceil((area / b.litter.per) * (s.build === 'IV' ? 1 : 1)));
      const hs = b.litter.handles;
      const per = Math.max(1, Math.ceil(packs / hs.length));
      hs.forEach((h, i) => { if (i === 0 || packs > 1) jobs.push(add(i === 0 ? 'Leaf litter' : 'Leaf litter (mix)', h, { qty: per, amount: `${per} pack${per > 1 ? 's' : ''}`, why: i === 0 ? 'Food and cover for the clean-up crew; keeps soil moist' : 'Mixing leaf types gives a longer-lasting layer' })); });
    }
    if (b.moss && b.moss.length) {
      const qty = b.habitat === 'tropical' ? Math.max(1, Math.round((d.L * d.W) / 4500)) : 1;
      jobs.push(add(b.mossLabel || 'Live moss', b.moss[0], { alt: b.moss.slice(1), qty, amount: `${qty} container${qty > 1 ? 's' : ''}`, optional: b.habitat !== 'tropical' && !b.mossLabel, why: b.mossLabel ? 'Holds humidity where it’s needed' : 'Ground cover that holds humidity' }));
    }
    for (const hsItem of b.hardscape) {
      if (hsItem.minH && d.H < hsItem.minH) continue;
      const dimCm = hsItem.dim === 'H' ? d.H : hsItem.dim === 'D' ? Math.sqrt(d.L * d.L + d.H * d.H) : d.L;
      const qty = typeof hsItem.qty === 'function' ? hsItem.qty(d) : hsItem.qty || 1;
      const opts = { alt: hsItem.alt, optional: hsItem.optional, why: hsItem.why, qty };
      if (hsItem.type === 'background') opts.background = true;
      else if (hsItem.type === 'size') opts.size = (dimCm * hsItem.frac) / 2.54;
      jobs.push(add(hsItem.slot, hsItem.handle, opts));
    }
    await Promise.all(jobs);
    if (token !== renderToken) return;

    // keep a stable order
    const order = ['Drainage layer', 'Bioactive soil', 'Sand for the mix', 'Sand (optional, firmer surface)', 'Top layer (optional)', 'Leaf litter', 'Leaf litter (mix)'];
    lines.sort((a, b2) => { const ia = order.indexOf(a.role), ib = order.indexOf(b2.role); return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib); });
    bundle = lines;
    out.innerHTML = planHTML(s, b, d, n, layers, lines);
    out.hidden = false;
    bindBundle();
    updateTotal();
  };

  const soilName = (b) => ({ abg: 'Jurassic ABG Terrarium Mix', arid: 'Jurassic ABG Semi-Arid Blend', tara: 'Jurassic ABG Tarantula Mix', coir: 'Coconut fibre' }[b.soilProduct] + (b.sandMix && !b.sandMix.optional ? ' + sand' : ''));
  const soilWhy = (b) => ({
    abg: 'Canadian-made bioactive soil from Jurassic Reptile Products – airy, plant-friendly and crew-friendly',
    arid: 'Canadian-made semi-arid blend from Jurassic Reptile Products – holds burrows, drains fast',
    tara: 'Canadian-made invert mix from Jurassic Reptile Products – holds burrows and moisture',
    coir: 'A thin, moisture-holding floor layer'
  }[b.soilProduct]);

  const layerHTML = (layers, d) => {
    const total = layers.reduce((t, l) => t + l.cm, 0);
    const scale = Math.min(14, 220 / total);
    return `<div class="pl-stack" aria-hidden="true">${layers.map((l) => `<div class="pl-stack__layer${l.thin ? ' is-thin' : ''}" style="height:${Math.max(l.thin ? 6 : 18, l.cm * scale)}px;background:${l.color}"></div>`).join('')}</div>
      <ol class="pl-layers">${layers.map((l) => `<li><span class="pl-swatch" style="background:${l.color}"></span><span><strong>${esc(l.name)}</strong>${l.key === 'mesh' ? '' : ` – ${l.key === 'moss' ? 'patches on the surface' : l.cm + ' cm'}`}${l.optional ? ' <em class="muted">(optional)</em>' : ''}</span></li>`).join('')}</ol>`;
  };

  const linePrice = (l) => l.picks.reduce((t, p) => t + p.variant.price * p.qty, 0);
  const planHTML = (s, b, d, n, layers, lines) => {
    const vol = Math.round((d.L * d.W * d.H) / 1000);
    const guides = b.guides.map((k) => GUIDES[k]).filter(Boolean);
    const clean = SHARED_CLEAN.concat(b.extraClean || []);
    return `
    <div class="pl-card pl-summary">
      <p class="eyebrow">Your plan</p>
      <h2 class="h3">${esc(s.name)} · ${b.label} build</h2>
      <dl class="pl-facts">
        <div><dt>Enclosure</dt><dd>${Math.round(d.L)} × ${Math.round(d.W)} × ${Math.round(d.H)} cm <span class="muted">(${vol} L)</span></dd></div>
        <div><dt>Animals</dt><dd>${s.max === 99 ? 'Colony' : n}${s.max === 1 && state.count > 1 ? ` <span class="muted">(per enclosure)</span>` : ''}</dd></div>
        <div><dt>Temperature</dt><dd>${esc(s.temp)}</dd></div>
        <div><dt>Humidity</dt><dd>${esc(s.hum)}</dd></div>
        <div><dt>Experience</dt><dd>${LEVELS[s.level]}</dd></div>
      </dl>
    </div>

    <div class="pl-card">
      <h3 class="h4">Layers, from top to bottom</h3>
      <div class="pl-layerwrap">${layerHTML(layers, d)}</div>
      <p class="small">${esc(b.layersNote)}</p>
      ${layers.some((l) => l.key === 'drain') ? `<p class="small muted">Put the clay balls in first, lay the mesh flat on top (pushed slightly up the glass), then add pre-moistened soil – it should clump when squeezed but not drip.</p>` : ''}
    </div>

    <div class="pl-card">
      <h3 class="h4">What you’ll need</h3>
      <ul class="pl-lines" data-pl-lines>
        ${lines.map((l, i) => l.missing ? `<li class="pl-line is-missing"><div class="pl-line__body"><strong>${esc(l.role)}</strong><span class="small muted">${esc(l.why)}</span></div></li>` : `
        <li class="pl-line">
          <label class="pl-line__check"><input type="checkbox" data-pl-line="${i}" ${l.optional ? '' : 'checked'}><span class="visually-hidden">Include in bundle</span></label>
          ${l.product.featured_image ? `<img src="${l.product.featured_image}&width=120" alt="" width="60" height="60" loading="lazy">` : ''}
          <div class="pl-line__body">
            <span class="pl-line__role">${esc(l.role)}${l.optional ? ' · optional' : ''}</span>
            <a href="/products/${l.handle}" class="pl-line__title">${esc(l.product.title)}</a>
            <span class="small">${l.picks.map((p) => `${p.qty} × ${p.variant.title === 'Default Title' ? 'pack' : esc(p.variant.title)}`).join(' + ')}${l.amount ? ` <span class="muted">· ${esc(l.amount)}</span>` : ''}</span>
            <span class="small muted">${esc(l.why)}</span>
          </div>
          <span class="pl-line__price">${money(linePrice(l))}</span>
        </li>`).join('')}
      </ul>
      <p class="small muted">Not sold by us but essential: a clean-up crew (springtails${s.group === 'I' && s.build === 'IV' ? '' : ' and isopods'}), live plants${b.habitat === 'arid' ? ' (hardy succulents)' : ''}, lighting${s.group === 'R' ? ', UVB and a thermostat-controlled heat source' : ''}.</p>
    </div>

    <div class="pl-card pl-bundle">
      <div>
        <p class="eyebrow">Your bundle</p>
        <p class="h3" data-pl-total>—</p>
        <p class="small muted" data-pl-items></p>
      </div>
      <button type="button" class="btn btn--clay btn--lg" data-pl-add>Add bundle to cart</button>
      <p class="tiny muted">Untick anything you already have. Quantities are estimates – your build may need a little more or less.</p>
    </div>

    <div class="pl-card">
      <h3 class="h4">Caring for your ${esc(s.name.toLowerCase())} build</h3>
      <div class="pl-care">
        ${[['Daily', b.care.daily], ['Weekly', b.care.weekly], ['Monthly', b.care.monthly], ['Every year or so', b.care.yearly]].map(([t, arr]) => `<div><h4>${t}</h4><ul>${arr.map((x) => `<li>${esc(x)}</li>`).join('')}</ul></div>`).join('')}
      </div>
      <h4 class="pl-subhead">Cleaning</h4>
      <ul class="pl-clean">${clean.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>
      <p class="small"><strong>Before adding your animal:</strong> let the build “grow in” for ${b.habitat === 'arid' ? '2–4' : '3–6'} weeks so plants root and the clean-up crew establishes.</p>
    </div>

    <div class="pl-card">
      <h3 class="h4">Guides for this build</h3>
      <ul class="pl-guides"><li><a href="/pages/bioactive-plant-planner?animal=${s.slug}" class="link-arrow"><strong>Best live plants for your ${esc(s.name.toLowerCase())} →</strong></a></li>${guides.map((g) => `<li><a href="${g.href}" class="link-arrow">${esc(g.title)} →</a></li>`).join('')}</ul>
      <p class="tiny muted">Care ranges are general hobby guidance, not veterinary advice. Always research your specific animal and check provincial and municipal rules before buying.</p>
    </div>`;
  };

  const bindBundle = () => {
    $$('[data-pl-line]').forEach((c) => c.addEventListener('change', updateTotal));
    $('[data-pl-add]').addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      const items = [];
      $$('[data-pl-line]:checked').forEach((c) => bundle[Number(c.dataset.plLine)].picks.forEach((p) => items.push({ id: p.variant.id, quantity: p.qty * (state.species.max === 1 && state.count > 1 ? state.count : 1) })));
      if (!items.length) return;
      btn.disabled = true; const txt = btn.textContent; btn.textContent = 'Adding…';
      try {
        if (window.OBAddToCart) await window.OBAddToCart(items);
        else { await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) }); location.href = '/cart'; }
        btn.textContent = 'Added ✓';
      } catch (err) { btn.textContent = 'Couldn’t add – try again'; }
      setTimeout(() => { btn.disabled = false; btn.textContent = txt; }, 2500);
    });
  };
  const updateTotal = () => {
    const mult = state.species && state.species.max === 1 && state.count > 1 ? state.count : 1;
    let total = 0, count = 0;
    $$('[data-pl-line]:checked').forEach((c) => { const l = bundle[Number(c.dataset.plLine)]; total += linePrice(l) * mult; count += l.picks.reduce((t, p) => t + p.qty, 0) * mult; });
    const t = $('[data-pl-total]'); if (t) t.textContent = money(total) + ' CAD';
    const it = $('[data-pl-items]'); if (it) it.textContent = `${count} item${count === 1 ? '' : 's'}${mult > 1 ? ` · for ${mult} enclosures` : ''}`;
  };

  /* ---------- Init from URL ---------- */
  const params = new URLSearchParams(location.search);
  const qs = params.get('species');
  if (qs && bySlug[qs]) {
    chooseSpecies(qs);
    if (params.get('n')) { countIn.value = params.get('n'); updateCount(); }
    const size = (params.get('size') || '').split('x').map(Number);
    if (size.length === 3 && size.every((x) => x > 0)) { const k = size.join('x'); presetSel.value = PRESETS.some((p) => p.join('x') === k) ? k : 'custom'; setDims(size); }
    else { const sug = suggest(); if (sug) { presetSel.value = sug.join('x'); setDims(sug); } }
    updateURL();
    render();
  }
})();
